/**
 * editor-image-tools (dpi) — PNG `data:` URL 의 물리 해상도(dpi)·픽셀 크기를 파싱해 "자동" 표시
 * 크기를 보정하기 위한 순수 유틸.
 *
 * 브라우저 <img> 는 PNG 의 dpi(pHYs) 메타를 **무시하고 naturalWidth(px)를 CSS px 로 1:1** 표시한다.
 * 반면 OS/이미지 도구는 의도한 논리 크기를 dpi 로 인코딩한다 — **macOS 는 72dpi 기준**(레티나
 * 캡처는 픽셀이 2배이면서 dpi=144=72×2), **그 외(Windows 등)는 96dpi 기준**. 따라서 이미지를
 * 의도한 논리 크기로 표시하려면 `naturalWidth × referenceDpi / dpi` 로 환산한다(referenceDpi 는
 * 실행 환경 OS 기준). devicePixelRatio 는 dpi·픽셀 양쪽에 이미 반영되어 referenceDpi 로 나누면
 * 상쇄되므로 공식에 직접 곱하지 않는다.
 *
 * `parsePngFormat` 은 IDAT(픽셀 데이터) 청크에서 즉시 중단하므로 이미지 크기와 무관하게 헤더~pHYs
 * 앞부분만 읽는다(실측 ~2µs). 따라서 매 로딩 시 재파싱해도 비용이 무시 가능 → dpi 는 메모리 캐시면
 * 충분하고 노드 attr 직렬화가 불필요하다.
 */

/// <reference path="../png-dpi-reader-writer.d.ts" />
import { parsePngFormat } from 'png-dpi-reader-writer'

/** macOS 가 이미지 dpi 를 기록하는 기준 해상도(레티나 캡처 = 72×2 = 144dpi). */
export const MAC_REFERENCE_DPI = 72
/** macOS 외(Windows 등) 기준 해상도 = 96dpi (W3C CSS reference pixel 과도 일치). */
export const DEFAULT_REFERENCE_DPI = 96

/** pHYs 청크는 항상 IHDR 직후·IDAT 이전에 위치하므로 앞부분만 디코드하면 충분. */
const HEADER_BYTES = 4096

export interface PngMeta {
  width: number
  height: number
  /** pHYs 청크의 dpi. 기록이 없으면 null(= 보정 불가, naturalWidth 유지). */
  dpi: number | null
}

/**
 * 실행 환경 OS 의 이미지 dpi 기준 — macOS 72, 그 외 96. 이미지 dpi 가 "어느 OS 기준으로 기록됐는지"는
 * 만든 OS 에 달려 있으나 이를 알 수 없으므로, 일반적인 "만든 OS = 보는 OS" 가정 하에 현재 navigator
 * 로 판정한다(cross-OS draft 는 어긋날 수 있는 차선). navigator 미가용(SSR/테스트) 시 96.
 */
export function resolveReferenceDpi(): number {
  if (typeof navigator === 'undefined') return DEFAULT_REFERENCE_DPI
  return /Mac/i.test(navigator.userAgent) ? MAC_REFERENCE_DPI : DEFAULT_REFERENCE_DPI
}

/**
 * `data:image/png;base64,...` 의 앞부분만 디코드해 width/height/dpi 를 파싱한다.
 * PNG(base64) 가 아니거나 width/height 를 못 읽으면 null. pHYs 부재 시 dpi 는 null.
 */
export function parsePngMetaFromDataUrl(src: string): PngMeta | null {
  const bytes = decodePngHeaderBytes(src)
  if (bytes == null) {
    return null
  }
  try {
    const info = parsePngFormat(bytes)
    if (info.width == null || info.height == null || info.width <= 0 || info.height <= 0) {
      return null
    }
    return { width: info.width, height: info.height, dpi: info.dpi ?? null }
  } catch {
    return null
  }
}

/**
 * dpi 보정 폭 — `naturalWidth × referenceDpi / dpi`. dpi 기록이 없거나(null) referenceDpi 와 같으면
 * 변화 없음. referenceDpi 는 `resolveReferenceDpi()`(macOS 72 / 그 외 96). 호출부에서 editorWidth clamp.
 */
export function dpiCorrectedWidth(
  naturalWidth: number,
  dpi: number | null,
  referenceDpi: number,
): number {
  if (naturalWidth <= 0 || dpi == null || dpi <= 0 || dpi === referenceDpi) {
    return naturalWidth
  }
  return Math.round((naturalWidth * referenceDpi) / dpi)
}

/** `data:[mime];base64,XXXX` 헤더 앞부분(HEADER_BYTES)만 Uint8Array 로 디코드. PNG base64 만. */
function decodePngHeaderBytes(src: string): Uint8Array | null {
  if (!src.startsWith('data:image/png')) {
    return null
  }
  const comma = src.indexOf(',')
  if (comma < 0 || !/;base64/i.test(src.slice(0, comma))) {
    return null
  }
  // HEADER_BYTES 바이트 ≈ ceil(n/3)*4 base64 문자. 4 의 배수로 잘라 atob 패딩 오류를 피한다.
  const charCount = Math.ceil(HEADER_BYTES / 3) * 4
  const base64 = src.slice(comma + 1, comma + 1 + charCount)
  try {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  } catch {
    return null
  }
}
